/* eslint-disable max-len */
/* eslint-disable camelcase */
import { CircularProgress, makeStyles, Tab, Tabs, Theme } from '@material-ui/core';
import { AppAPI } from '@moderepo/mode-apis';
import { FontIcon, Time, TimeSpan } from '@moderepo/mode-app-components';
import { selectEntityById, setEntity, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { Entity, MetricsReportSubscription, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation, addControlPanelComp, removeControlPanelComp, ControlPanelCompPriority, setCache,
    selectCache } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce, { castDraft, Draft } from 'immer';
import moment from 'moment';
import React, { CSSProperties, useCallback, useMemo, useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DEFAULT_ENTITY_VIEW_DISPLAY_NAME_KEY, EntityDetailsTimeSpanControlBarContainer, FetchEntityTimeSpanDataParams,
    FetchEntityTimeRangeDataParams, useGetEntityClassViewsConfig, getDataParamsForFixedTimeRange, getPreviousTimestamp,
    DEFAULT_MIN_TIMESTAMP_INPUTS, useSelectTranslatedEntityClasses, getDataParamsForFixedTimeSpan } from '.';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { ReadonlyNonEmptyArray } from '../../utils/utils';
import { EntityDetailsTimeSpanControlSettings, FixedTimeRangeControlSettings, FixedTimeSpanControlSettings, isEntityDetailsTimeSpanControlSettings,
    isFixedTimeRangeControlSettings, isFixedTimeSpanControlSettings, isTimeRangeControlSettings, TimeControlType, TimeRangeBase,
    TimeRangeControlBarContainer, TimeRangeControlSettings } from '../misc';
import { SelectEntityCompOptions, EntityPreviewCompOptions, SelectEntitySubscriptionCompOptions, SlideOutPanelCompType }
    from '../routes/defaultProjectConfig';
import { RouteHelper } from '../routes/RouteHelper';
import { EntityDetailsBreadcrumbBar } from './EntityDetailsBreadcrumbBar';
import { EntityDetailsContent } from './EntityDetailsContent';
import { isRootEntity, PredefinedTimeRangeType } from './utils';
import { EntityViewConfig } from './EntityDetailsLayoutUtils';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
            display      : 'flex',
            flexDirection: 'column',
            position     : 'relative',
        },

        breadcrumbAndControlBar: {
            backgroundColor   : theme.palette.background.default,
            '& $breadcrumbBar': {
                padding: theme.spacing(1),
            },
            '& $controlBar': {
                padding: theme.spacing(0, 1, 1, 1),
            },
        },

        breadcrumbBar: {
        },

        controlBar: {
        },

        titleContainer: {
            display        : 'flex',
            alignItems     : 'center',
            justifyContent : 'center',
            flexDirection  : 'column',
            '&:not(:empty)': {
                paddingBottom: theme.spacing(1),
            },
        },

        title: {
            fontSize                    : 24,
            fontWeight                  : 500,
            color                       : theme.palette.text.primary,
            [theme.breakpoints.up('md')]: {
                fontSize: '26px',
            },
        },

        viewTabs: {
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'flex-end',
        },


        entityDetailsContent: {
            zIndex: 1,
        },

        [theme.breakpoints.up('md')]: {
            breadcrumbAndControlBar: {
                '& $breadcrumbBar': {
                    padding: theme.spacing(2),
                },
                '& $controlBar': {
                    padding: theme.spacing(0, 2, 2, 2),
                },
            },

            titleContainer: {
                flexDirection  : 'row',
                justifyContent : 'space-between',
                '&:not(:empty)': {
                    paddingBottom: theme.spacing(2),
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            breadcrumbAndControlBar: {
                '& $breadcrumbBar': {
                    padding: theme.spacing(2, 3, 2, 3),
                },
                '& $controlBar': {
                    padding: theme.spacing(0, 3, 2, 3),
                },
            },

            titleContainer: {
                '&:not(:empty)': {
                    paddingBottom: theme.spacing(3),
                },
            },
        },
    };
}, { name: 'EntityDetailsContainer', index: 1 });


const ENTITY_DETAILS_MAIN_CONTROL_BAR = 'Entity_Details_Control_Bar';


// The cache key we will be using to store selected timeSpan value in the uiState for report view so that we can continue using the same
// selected time span when the user leave a report page and then come back
const REPORT_VIEW_TIME_SPAN_STATE_CACHE_KEY = 'entity_details_report_view_time_span_state';


/**
 * Custom hook function to get the params from URL, specifically params used for fetching data. To allow the operator A to share entity details page
 * URL to another operator, operator B, and have the page show EXACTLY the same content for the operator B, we have to store the params used
 * for fetching the data for the entity details page in the URL. For example the 'timeSpan' and 'timestamps' params. So when the operator B
 * clicks on the link, we will be able to retrieve the params from the URL and fetch data for the exact params.
 *
 * This hook is used for retrieving the params stored in the URL.
 */
const useGetFetchDataParamsFromURL = (): {
    readonly viewIndex: number | undefined;
    readonly timeSpan: TimeSpan | undefined;
    readonly timestamps: readonly number[] | undefined;
    readonly startTime: number | undefined;
    readonly endTime: number | undefined;
    readonly aggregation: TimeSeriesAggregation | undefined;
} => {
    const [searchParams] = useSearchParams();

    const result = useMemo(() => {
        // Convert the searchParams to plain Object so that it is easier to use
        const queryParams = Array.from(searchParams.entries()).reduce((result: {[key: string]: string}, [key, value]) => {
            return {
                ...result,
                [key]: value,
            };
        }, { });

        // Get the viewIndex from the query params and make sure it is a valid number
        const viewIndex = queryParams.viewIndex !== undefined
        && !Number.isNaN(Number(queryParams.viewIndex)) && Number(queryParams.viewIndex) >= 0
        && Number.isInteger(Number(queryParams.viewIndex))
            ? Number(queryParams.viewIndex) : undefined;

        // Get the timeSpan from the query params and make sure it is a valid time span value
        const timeSpan = queryParams.timeSpan && Object.values(TimeSpan).includes(queryParams.timeSpan as TimeSpan)
            ? queryParams.timeSpan as TimeSpan
            : undefined;

        // Get the array of timestamps values from the query params and make sure they are all valid timestamps
        const timestamps = queryParams.timestamps && queryParams.timestamps.split(',').find((timestamp) => {
            // Return TRUE if this is NOT a number
            return Number.isNaN(Number(timestamp)) || Number(timestamp) < 0 || Number(timestamp) > Date.now() + Time.YEAR_IN_MS;
        }) === undefined
            ? queryParams.timestamps.split(',').map((timestamp) => {
                // If the array of timestamps are valid numbers, convert them to numbers
                return Number(timestamp);
            })
            : undefined;

        // Get the startTime from the query params and make sure it is a valid time value
        const startTime = queryParams.startTime !== undefined
        && !Number.isNaN(Number(queryParams.startTime)) && Number(queryParams.startTime) >= 0
        && Number(queryParams.startTime) <= Date.now() + Time.YEAR_IN_MS
            ? Number(queryParams.startTime)
            : undefined;

        // Get the endTime from the query params and make sure it is a valid time value
        const endTime = queryParams.endTime !== undefined
        && !Number.isNaN(Number(queryParams.endTime)) && Number(queryParams.endTime) >= 0
        && Number(queryParams.endTime) <= Date.now() + Time.YEAR_IN_MS
            ? Number(queryParams.endTime)
            : undefined;

        // Get the timeSpan from the query params and make sure it is a valid time span value
        const aggregation = queryParams.aggregation
        && Object.values(TimeSeriesAggregation).includes(queryParams.aggregation as TimeSeriesAggregation)
            ? queryParams.aggregation as TimeSeriesAggregation
            : undefined;

        return {
            viewIndex,
            timeSpan,
            timestamps,
            startTime,
            endTime,
            aggregation,
        };
    }, [searchParams]);

    return result;
};



/**
 * Create a new time span params base on the provided settings
 */
const createInitialTimeSpanDataParams = (
    timeControlSettings: EntityDetailsTimeSpanControlSettings,
    timeRange: {
        readonly startTime: number;
        readonly endTime: number;
    } | undefined,
) => {
    // First, get the Default timeSpan
    const defaultTimeSpan = timeControlSettings.defaultTimeSpan ?? TimeSpan.DAY;

    // If "enabledTimeSpans" is set, make sure the "defaultTimeSpan" is one of the "enabledTimeSpans". If it is not then use the first
    // timeSpan defined in "enabledTimeSpans".
    const timeSpan = timeControlSettings.enabledTimeSpans && !timeControlSettings.enabledTimeSpans.includes(defaultTimeSpan)
        ? timeControlSettings.enabledTimeSpans[0] : defaultTimeSpan;

    // Get the time to start with. If timeRange.startTime is provided, use that as the start time. Else use NOW as the startTime.
    const startTime = timeRange?.startTime || Date.now();

    // Add some offset to the startTime based on the 'timeControlSettings.defaultTimeSpanOffset`.
    // We will only add the offset if the startTimestamp was NOT initialized with the timeRange value.
    const offset = timeRange?.startTime === undefined ? timeControlSettings.defaultTimeSpanOffset ?? 0 : 0;
    const startTimestamp = moment(startTime).add(offset, timeSpan).startOf(timeSpan);

    const minTimestamps = timeControlSettings.minTimestampInputs ?? DEFAULT_MIN_TIMESTAMP_INPUTS;

    // Initialize the timestamps base on the timeControlSettings.
    const initialTimestamps = (new Array(minTimestamps)).fill(0).map((value, index) => {
        // Get the previous Nth timeSpan from the startTimestamp
        return getPreviousTimestamp(timeSpan, startTimestamp.valueOf(), index);
    }) as unknown as ReadonlyNonEmptyArray<number>;

    return {
        timeSpan,
        timestamps: initialTimestamps,
    };
};


/**
 * Get the timestamp base on the 'timeBase' value and offset. For example: If the current time is "2022-09-26T05:45:28.16Z"
 * If baseTime is START_FROM_BEGINNING_OF_HOUR or END_AT_BEGINNING_OF_HOUR, return "2022-09-26T05:00:00.00Z"
 * If baseTime is START_FROM_BEGINNING_OF_DAY or END_AT_BEGINNING_OF_DAY, return "2022-09-26T00:00:00.00Z"
 * If baseTime is START_FROM_BEGINNING_OF_WEEK or END_AT_BEGINNING_OF_WEEK, return "2022-09-25T00:00:00.00Z" (25th is Sunday, first day of the week)
 * If baseTime is START_FROM_BEGINNING_OF_MONTH or END_AT_BEGINNING_OF_MONTH, return "2022-09-01T00:00:00.00Z"
 *
 * This function will also add "timeBaseOffset" to the result. So if "timeBaseOffset" is set to 2 and timeBase is XXX_BEGINNING_OF_DAY, the
 * function will add 2 days to the result. If timeBase ise XXX_BEGINNING_OF_WEEK, it will add 2 weeks to the result. And so on...
 */
const getTimeBaseValue = (timeBase: TimeRangeBase, timeBaseOffset: number = 0): number => {
    switch (timeBase) {
        case TimeRangeBase.START_FROM_BEGINNING_OF_HOUR:
        case TimeRangeBase.END_AT_BEGINNING_OF_HOUR:
            return moment().startOf('hours').add(timeBaseOffset, 'hours').valueOf();
        case TimeRangeBase.START_FROM_BEGINNING_OF_DAY:
        case TimeRangeBase.END_AT_BEGINNING_OF_DAY:
            return moment().startOf('days').add(timeBaseOffset, 'days').valueOf();
        case TimeRangeBase.START_FROM_BEGINNING_OF_WEEK:
        case TimeRangeBase.END_AT_BEGINNING_OF_WEEK:
            return moment().startOf('weeks').add(timeBaseOffset, 'weeks').valueOf();
        default:
            // We can assume timeBase is START_FROM_BEGINNING_OF_MONTH or END_AT_BEGINNING_OF_MONTH
            return moment().startOf('months').add(timeBaseOffset, 'months').valueOf();
    }
};


/**
 * Given start and end time, return new start/end time based on the defaultRangeInHours and maxRangeInHours
 */
const getInitialStartEndTime = (
    timeBase: TimeRangeBase | undefined,
    timeBaseOffset: number | undefined,
    defaultRangeInHours: number | undefined,
    maxRangeInHours: number | undefined,
): {
    readonly initialStartTime: number;
    readonly initialEndTime: number;
} => {

    // Use the "defaultRangeInHours" or "maxRangeInHours" or 1 hours as the range between start/end time
    const range = defaultRangeInHours ?? maxRangeInHours ?? 1;

    // If timeBase is provided and if it is one of the START_FROM_BEGINNING_OF_XXX then we will set the startTime to be the
    // beginning of the time unit (hours, days, weeks, months) and then add N (range) hours to the startTime to get endTime
    if (
        timeBase === TimeRangeBase.START_FROM_BEGINNING_OF_HOUR
        || timeBase === TimeRangeBase.START_FROM_BEGINNING_OF_DAY
        || timeBase === TimeRangeBase.START_FROM_BEGINNING_OF_WEEK
        || timeBase === TimeRangeBase.START_FROM_BEGINNING_OF_MONTH
    ) {
        const initialStartTime = getTimeBaseValue(timeBase, timeBaseOffset ?? 0);
        const initialEndTime = moment(initialStartTime).add(range, 'hours').valueOf();

        return {
            initialStartTime,
            initialEndTime,
        };
    }

    // If timeBase is provided and if it is one of the END_AT_BEGINNING_OF_XXX then we will set the endTime to be the
    // beginning of the time unit (hours, days, weeks, months) and then subtract N (range) hours to the endTime to get startTime
    if (
        timeBase === TimeRangeBase.END_AT_BEGINNING_OF_HOUR
        || timeBase === TimeRangeBase.END_AT_BEGINNING_OF_DAY
        || timeBase === TimeRangeBase.END_AT_BEGINNING_OF_WEEK
        || timeBase === TimeRangeBase.END_AT_BEGINNING_OF_MONTH
    ) {
        const initialEndTime = getTimeBaseValue(timeBase, timeBaseOffset ?? 0);
        const initialStartTime = moment(initialEndTime).subtract(range, 'hours').valueOf();

        return {
            initialStartTime,
            initialEndTime,
        };
    }

    // There is no timeBase to based on so we will use NOW as the endTime and subtract N hours from endTime to get startTime
    const initialEndTime = moment();

    return {
        initialStartTime: moment(initialEndTime).subtract(range, 'hours').valueOf(),
        initialEndTime  : initialEndTime.valueOf(),
    };
};


/**
* Create new time range based on the provided settings
*/
const createInitialTimeRangeDataParams = (
    timeControlSettings: TimeRangeControlSettings,
    timeSpan: {
        readonly timeSpan: TimeSpan;
        readonly timestamps: ReadonlyNonEmptyArray<number>;
    } | undefined,
) => {
    // If timeSpan is provided, convert the timeSpan values to time range values
    if (timeSpan?.timestamps && timeSpan.timestamps.length > 0) {
        const startTime = timeSpan.timestamps[0];
        const endTime = moment(startTime).add(1, timeSpan.timeSpan).valueOf();
        return {
            startTime,
            endTime,
        };
    }

    const { initialStartTime, initialEndTime } = getInitialStartEndTime(
        timeControlSettings.timeBase, timeControlSettings.timeBaseOffset,
        timeControlSettings.defaultRangeInHours, timeControlSettings.maxRangeInHours,
    );

    return {
        startTime: initialStartTime,
        endTime  : initialEndTime,
    };
};



export interface EntityDetailsContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;
    readonly viewIndex?: number | undefined;

    // Whether or not to display the control bar at the top of the page. Default is FALSE.
    readonly stickyControlBar?: boolean | undefined;
}



/**
 * This component display info for 1 entity. It contains the entity breadcrumb, control bar, and a component that will take care of
 * displaying the entity's UI components e.g. children KPI, metrics, ranking tables, etc...
 */
export const EntityDetailsContainer: React.FC<EntityDetailsContainerProps> = (props: EntityDetailsContainerProps) => {

    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:appDataState, dispatch:appDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const navigate = useNavigate();


    // Get the timeSpan and timestamps and other info needed for fetching data from the URL params
    const paramsFromURL = useGetFetchDataParamsFromURL();


    // All of project's entity classes
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Local state for keeping track of the current selected entityId, viewIndex, and time control's params.
    const [currentViewStates, setCurrentViewStates] = useState<{
        readonly selectedEntityId: string;
        readonly selectedViewIndex: number;
        readonly timeSpanParams?: FetchEntityTimeSpanDataParams | undefined;
        readonly timeRangeParams?: FetchEntityTimeRangeDataParams | undefined;
    }>({
        selectedEntityId : props.entityId,
        selectedViewIndex: paramsFromURL.viewIndex ?? props.viewIndex ?? 0,
        timeSpanParams   : paramsFromURL.timeSpan && paramsFromURL.timestamps && paramsFromURL.timestamps.length > 0
            ? { timeSpan: paramsFromURL.timeSpan, timestamps: paramsFromURL.timestamps as ReadonlyNonEmptyArray<number> }
            : undefined,
        timeRangeParams: paramsFromURL.startTime !== undefined && paramsFromURL.endTime !== undefined
            ? { startTime: paramsFromURL.startTime, endTime: paramsFromURL.endTime, aggregation: paramsFromURL.aggregation }
            : undefined,
    });

    const {
        selectedEntityId,
        selectedViewIndex,
        timeSpanParams,
        timeRangeParams,
    } = currentViewStates;



    // Fetch the Entity object for the selectedEntityId
    const selectedEntity = useSelectEntity(props.projectId, selectedEntityId, false);

    // A state to keep track of the entities the user navigated to in the hierarchy. The first entity in the array will be the first entity
    // the user see when they enter the page, the entity specified in the props.entityId
    // However, the first entity might not be the root which mean the user can't go back to the first entity's parent. We will have some UI
    // to allow the user to go up the entity tree until they reach the root.
    const [entitiesBreadcrumbs, setEntitiesBreadcrumbs] = useState<readonly Entity[]>([]);


    // Find the entityClass object that the selectedEntity belonged to
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    // Use the custom hook to get all the views configured for the selectedEntityClass
    const entityClassViewsConfig = useGetEntityClassViewsConfig(props.projectId, selectedEntityClass);


    // An entity details page can have 1 or more views so when the selectedViewIndex changed, get the actual view config object for that index
    const selectedViewConfig = useMemo((): EntityViewConfig | undefined => {
        return entityClassViewsConfig[Math.min(selectedViewIndex, entityClassViewsConfig.length - 1)];
    }, [entityClassViewsConfig, selectedViewIndex]);



    /**
     * Get the timeControl settings for the current selected view. Each view can have a customized time control so if it is provided
     * then use the custom control. If not then use the default.
     */
    const timeControlSettings = useMemo((): EntityDetailsTimeSpanControlSettings | TimeRangeControlSettings
    | FixedTimeSpanControlSettings | FixedTimeRangeControlSettings | undefined => {

        if (selectedViewConfig) {
            if (selectedViewConfig.viewSettings.isReportView !== false) {
                // if the view.isReportView is NOT specifically set to false then it will be true by default. And for report view,
                // we will always use the TIME_SPAN control no matter what the viewSettings.timeControl is set to

                // if selectedViewConfig.viewSettings.timeControl is an OBJECT containing customization for the Time_Span control then return it
                if (isEntityDetailsTimeSpanControlSettings(selectedViewConfig.viewSettings.timeControl)) {
                    return selectedViewConfig.viewSettings.timeControl;
                }
                // Else, return a new Time_Span settings object with the 'type' attribute and set 'defaultTimeSpanOffset' to -1.
                // For report views, we don't want to see the latest timeSpan because the data is not available until the end of the timeStan
                // We want to show the data for -1 timeSpan ago
                return {
                    type                 : TimeControlType.TIME_SPAN,
                    defaultTimeSpanOffset: -1,
                };
            }

            // Else, use whatever control is configured in the viewSettings.timeControl. NOTE: 'timeControl' can be a STRING name of one of the
            // time control types OR it can be an OBJECT containing custom settings for the time control.

            if (selectedViewConfig.viewSettings.timeControl === TimeControlType.TIME_SPAN) {
                return {
                    type: TimeControlType.TIME_SPAN,
                };
            }

            // if selectedViewConfig.viewSettings.timeControl is just a STRING value 'Time_Range' then return a new Object with just the 'type' attr
            if (selectedViewConfig.viewSettings.timeControl === TimeControlType.TIME_RANGE) {
                return {
                    type: TimeControlType.TIME_RANGE,
                };
            }

            if (selectedViewConfig.viewSettings.timeControl === TimeControlType.FIXED_TIME_SPAN) {
                return {
                    type: TimeControlType.FIXED_TIME_SPAN,
                };
            }

            if (selectedViewConfig.viewSettings.timeControl === TimeControlType.FIXED_TIME_RANGE) {
                return {
                    type: TimeControlType.FIXED_TIME_RANGE,
                };
            }

            // if selectedViewConfig.viewSettings.timeControl is an OBJECT containing customization for the 'Time_Span', 'Time_Range',
            // or 'Fixed_Time_Range' control then return it
            if (isEntityDetailsTimeSpanControlSettings(selectedViewConfig.viewSettings.timeControl)
                || isTimeRangeControlSettings(selectedViewConfig.viewSettings.timeControl)
                || isFixedTimeSpanControlSettings(selectedViewConfig.viewSettings.timeControl)
                || isFixedTimeRangeControlSettings(selectedViewConfig.viewSettings.timeControl)
            ) {
                return selectedViewConfig.viewSettings.timeControl;
            }
        }
        return undefined;
    }, [selectedViewConfig]);



    /**
     * This useEffect will monitor the current timeControlSettings to see which one is being used and then clear data for the other time
     * controls since they ate not needed anymore. This is to remove the unnecessary params in the URL.
     */
    useEffect(() => {
        // If View Config is not loaded then don't do anything. Wait until the view is loaded.
        if (!selectedViewConfig) {
            return;
        }

        if (isTimeRangeControlSettings(timeControlSettings)) {
            // The Time Range control is being used, clear the params for the Time Span control and get a new time range params
            setCurrentViewStates((currentState) => {

                // Create a new Time Range params based on the data we current data have
                const newTimeRangeParams = (() => {

                    // If we already have time range data then continue using the same time range data. This is the case when the user goes from
                    // one view to another and both uses the Time Range control
                    if (currentState.timeRangeParams) {
                        return currentState.timeRangeParams;
                    }

                    // If we don't have the time range data yet then create a new one. We can create a new time range data based on the data
                    // from the URL OR from the TIME SPAN data. If we have TIME SPAN data, we can kind of convert it to time range but it won't be perfect
                    const hasParamsFromURL = paramsFromURL.startTime !== undefined && paramsFromURL.endTime !== undefined;
                    const initialTimeRangeDataParams: FetchEntityTimeRangeDataParams = hasParamsFromURL
                        ? {
                            startTime: paramsFromURL.startTime,
                            endTime  : paramsFromURL.endTime,
                        }
                        : createInitialTimeRangeDataParams(timeControlSettings, currentState.timeSpanParams);

                    return initialTimeRangeDataParams;
                })();


                return produce(currentState, (draft) => {
                    draft.timeSpanParams = undefined;
                    draft.timeRangeParams = newTimeRangeParams;
                });
            });
        } else if (isEntityDetailsTimeSpanControlSettings(timeControlSettings)) {
            // The Time Span control is being used, clear the params for the Time Range control and get the new time span params

            setCurrentViewStates((currentState) => {

                // Create a new Time Span params based on the data we current data have
                const newTimeSpanDataParams = (() => {

                    // Get the cached timeSpan from uiState
                    const cachedTimeSpan = selectCache(uiState, REPORT_VIEW_TIME_SPAN_STATE_CACHE_KEY);

                    if (currentState.timeSpanParams) {
                        // If we already have time span data then continue using the same time span params. This is the case when the user goes from
                        // one view to another and both uses the Time Span control
                        return produce(currentState.timeSpanParams, (draft) => {
                            // If "maxTimestampInputs" is set, we can't use the currentValue yet. We need to make sure the new timeControlSettings
                            // support the same number of timestamp inputs. If the previous view has 2 timestamps inputs but this new view only
                            // allow 1 timestamp inputs, we need to trim the currentValue.timestamps so that it does have more than maxTimestampInputs
                            if (
                                timeControlSettings.maxTimestampInputs !== undefined && timeControlSettings.maxTimestampInputs < draft.timestamps.length
                            ) {
                                const newTimestamps = draft.timestamps.slice(0, timeControlSettings.maxTimestampInputs);
                                draft.timestamps = castDraft(newTimestamps as unknown as ReadonlyNonEmptyArray<number>);
                            }

                            // If "enabledTimeSpans" is configured but "currentValue.timeSpan" is not in one of the "enabledTimeSpans" then use the
                            // FIRST value from "enabledTimeSpans"
                            if (timeControlSettings.enabledTimeSpans && !timeControlSettings.enabledTimeSpans.includes(draft.timeSpan)) {
                                draft.timeSpan = timeControlSettings.enabledTimeSpans[0];
                            }
                        });
                    }

                    if (selectedViewConfig?.viewSettings.isReportView && cachedTimeSpan) {
                        // If the current view is Report view AND if we have a cachedTimeSpan from the UI State, use the cachedTimeSpan
                        // However, need to check if the "enabledTimeSpans" is configured. If the "enabledTimeSpans" is configured and the
                        // timeSpan found in cache is NOT one of the "enabledTimeSpans" then we will use the first timeSpan in the "enabledTimeSpans"
                        if (timeControlSettings.enabledTimeSpans && !timeControlSettings.enabledTimeSpans.includes(cachedTimeSpan.timeSpan)) {
                            return {
                                ...cachedTimeSpan,
                                timeSpan: timeControlSettings.enabledTimeSpans[0],
                            };
                        }
                        return cachedTimeSpan;
                    }

                    // If we don't have the time span data yet then create a new one. We can create a new time span data based on the data from the
                    // URL OR from the TIME RANGE data. If we have TIME RANGE data, we can kind of convert it to time span but it won't be perfect
                    const hasParamsFromURL = paramsFromURL.timeSpan && paramsFromURL.timestamps && paramsFromURL.timestamps.length > 0;
                    const initialTimeSpanDataParams: FetchEntityTimeSpanDataParams = hasParamsFromURL
                        ? {
                            timeSpan  : paramsFromURL.timeSpan,
                            timestamps: paramsFromURL.timestamps as unknown as ReadonlyNonEmptyArray<number>,
                        }
                        : createInitialTimeSpanDataParams(timeControlSettings, currentState.timeRangeParams);

                    return initialTimeSpanDataParams;
                })();


                return produce(currentState, (draft) => {
                    draft.timeRangeParams = undefined;
                    draft.timeSpanParams = newTimeSpanDataParams;
                });
            });
        } else {
            // No time control is being used, clear the params for both the Time Range and Time Span control
            setCurrentViewStates((currentState) => {
                return produce(currentState, (draft) => {
                    draft.timeSpanParams = undefined;
                    draft.timeRangeParams = undefined;
                });
            });
        }
    }, [paramsFromURL.endTime, paramsFromURL.startTime, paramsFromURL.timeSpan, paramsFromURL.timestamps, selectedEntityId, selectedViewConfig,
        selectedViewIndex, timeControlSettings, uiState]);



    /**
     * Update the URL. Replace the entityId in the URL with the new entityId and add the current selected timeSpan and timestamps to the URL.
     * We update the URL so that the current state is persisted and the user can reload the page and able to see the same UI as before.
     * Also, the user can share the link with someone and they can see the same thing.
     */
    const updateRouteURL = useCallback((
        entityId: string,
        viewIndex: number,
        timeSpanParams?: FetchEntityTimeSpanDataParams,
        timeRangeParams?: FetchEntityTimeRangeDataParams,
        replace?: boolean,
    ) => {
        navigate({
            pathname: RouteHelper.getInstance().getEntityDetailsPageUrl(props.projectId, entityId),
            search  : (new URLSearchParams({
                viewIndex: viewIndex.toString(),
                ...(timeSpanParams ? {
                    timeSpan  : timeSpanParams.timeSpan,
                    timestamps: timeSpanParams.timestamps.join(','),
                } : undefined),
                ...(timeRangeParams ? {
                    startTime  : timeRangeParams.startTime.toString(),
                    endTime    : timeRangeParams.endTime.toString(),
                    aggregation: timeRangeParams.aggregation,
                } : undefined),
            })).toString(),
        }, {
            state: { isActive: true },
            replace,
        });
    }, [navigate, props.projectId]);



    /**
     * On new entity selected, we will update the current selectedEntityId which will trigger the UI to be updated based on the
     * layout of the new selected entity.
     */
    const onEntitySelected = useCallback((
        entityTarget: Entity | string,
        viewIndex?: number,
        startTime?: number,
        endTime?: number,
    ) => {
        const entityId = typeof entityTarget === 'string' ? entityTarget : entityTarget.entityId;
        setCurrentViewStates((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                draft.selectedEntityId = entityId;
                draft.selectedViewIndex = viewIndex ?? 0;

                // If the caller provided start/end time then update the timeParams as well.
                if (startTime !== undefined && endTime !== undefined) {
                    if (draft.timeSpanParams) {
                        // The current time control is timeSpan BUT we need to show the entity details at a specific time range start -> end,
                        // Therefore, we need to convert that time range to time span value. So, we will calculate the different between the
                        // start and end time and then pick DAY, WEEK, or MONTH timeSpan accordingly to the range.
                        const range = endTime - startTime;
                        if (range >= Time.MONTH_IN_MS) {
                            draft.timeSpanParams.timeSpan = TimeSpan.MONTH;
                        } else if (range >= Time.WEEK_IN_MS) {
                            draft.timeSpanParams.timeSpan = TimeSpan.WEEK;
                        } else {
                            draft.timeSpanParams.timeSpan = TimeSpan.DAY;
                        }
                        draft.timeSpanParams.timestamps = [moment(startTime).startOf(draft.timeSpanParams.timeSpan).valueOf()];
                    } else if (draft.timeRangeParams) {
                        // If the current time control is time range then can just replace the control's start/end time with the provided
                        // start/end time
                        draft.timeRangeParams.startTime = startTime;
                        draft.timeRangeParams.endTime = endTime;
                    } else {
                        // If no time control is being used, then we will use time range
                        draft.timeRangeParams = {
                            startTime, endTime,
                        };
                    }
                }
            });
        });
    }, []);



    /**
     * This useEffect is used for updating the URL whenever the entityId, viewIndex, and time span or time range params changed.
     */
    useEffect(() => {
        updateRouteURL(selectedEntityId, selectedViewIndex, timeSpanParams, timeRangeParams, true);
    }, [selectedEntityId, timeRangeParams, timeSpanParams, selectedViewIndex, updateRouteURL]);



    /**
     * Fetch the ancestors for a given entity and store them in the array entities breadcrumb
     */
    const fetchEntityAncestors = useCallback(async (currentEntity: Entity) => {
        /**
         * Given an entity, recursively fetch it's parent, then grandparent, and so on until we hit the root entity. Get all the parent entities
         * we fetched into the result array
         */
        const fetchParentEntityRecursive = async (projectId: number, entity: Entity, result: Entity[]): Promise<Entity[]> => {
            if (isRootEntity(entity)) {
                // We are done
                return result;
            }

            // If we are not at the root, make an API call to fetch the entity's parent and add the parent entity to the beginning of the result
            // But first, check if we already have the parent entity in cache
            const parent = selectEntityById(appDataState, props.projectId, entity.parentId as string)   // get from cache
                || await AppAPI.getInstance().getEntityById(projectId, entity.parentId as string);      // if not in cache, make API call

            // Since we called the API directly to fetch entity data, we should add the fetched entity to the data state so that we can reuse it
            appDispatch(setEntity(props.projectId, parent));

            result.unshift(parent);

            // Recursively call fetchParentEntityRecursive again to fetch the parent of the new parent entity
            return fetchParentEntityRecursive(projectId, parent, result);
        };

        const result: Entity[] = await fetchParentEntityRecursive(props.projectId, currentEntity, [currentEntity]);
        setEntitiesBreadcrumbs(result);
    }, [appDataState, appDispatch, props.projectId]);


    /**
     * This useEffect is used for fetching the selected entity's ancestors so that we can build the breadcrumb.
     */
    useEffect(() => {
        if (selectedEntity) {
            fetchEntityAncestors(selectedEntity);
        }
    }, [fetchEntityAncestors, selectedEntity]);



    /**
     * On "Entity Tree" button clicked. We will open the slide out panel to show the entity tree.
     */
    const onShowEntitiesTreeButtonClick = useCallback(() => {
        const action: SelectEntityCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.SELECT_ENTITY,
            compProps: {
                data: {
                    projectId: props.projectId,
                },
                onEntitySelected,
            },
        };
        uiDispatch(showSlideOutPanel(action));
    }, [onEntitySelected, props.projectId, uiDispatch]);



    /**
     * On "Entity Info" button clicked from the breadcrumb bar. We will open the slide out panel to show the entity's info.
     */
    const onShowEntityInfoButtonClick = useCallback((entityTarget: Entity | string) => {
        const entityId = typeof entityTarget === 'string' ? entityTarget : entityTarget.entityId;
        const action: EntityPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ENTITY_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    entityId,
                },
                // Let this container to handle the "Show Entity Details" click because the this container will append
                // timeSpan and timestamp params to the URL, not just take the user to the entityDetails page.
                onShowEntityDetailsHandler: onEntitySelected,
            },
        };
        uiDispatch(showSlideOutPanel(action));
    }, [onEntitySelected, props.projectId, uiDispatch]);


    /**
     * On "Subscription" button clicked from the breadcrumb bar. We will open the slide out panel to show all the subscriptions and
     * ask the user to select one. When the user selected a subscription, we will show the entity detailed linked to the selected subscription
     */
    const onShowEntitySubscriptionButtonClick = useCallback(() => {
        const action: SelectEntitySubscriptionCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.SELECT_ENTITY_SUBSCRIPTION,
            compProps: {
                data: {
                    projectId: props.projectId,
                },
                onSubscriptionSelected: ((subscription: MetricsReportSubscription) => {
                    onEntitySelected(subscription.entityId);
                }),
            },
        };
        uiDispatch(showSlideOutPanel(action));
    }, [onEntitySelected, props.projectId, uiDispatch]);



    // Handler for the entity view tab click
    const onEntityViewTabChange = useCallback((event, index: number) => {
        setCurrentViewStates((currentValue) => {
            return produce(currentValue, (draft: Draft<typeof currentValue>) => {
                draft.selectedViewIndex = index;
            });
        });
    }, []);



    /**
     * On time range control input changed. We will update the TimeSpanParams.
     */
    const onTimeSpanControlInputsChanged = useCallback((timeSpan: TimeSpan, timestamps: ReadonlyNonEmptyArray<number>) => {
        setCurrentViewStates((currentState) => {
            return produce(currentState, (draft) => {
                if (timestamps && timestamps.length > 0) {
                    if (!draft.timeSpanParams) {
                        draft.timeSpanParams = {
                            timeSpan,
                            timestamps: castDraft(timestamps),
                        };
                    } else {
                        draft.timeSpanParams.timeSpan = timeSpan;
                        draft.timeSpanParams.timestamps = castDraft(timestamps);
                    }
                }
            });
        });


        if (selectedViewConfig?.viewSettings.isReportView) {
            // save the timeSpan in uiState so if the user come back to this page, we can use the same timeSpan
            uiDispatch(setCache(REPORT_VIEW_TIME_SPAN_STATE_CACHE_KEY, {
                timeSpan,
                timestamps,
            }));
        }
    }, [selectedViewConfig?.viewSettings.isReportView, uiDispatch]);


    /**
     * On time range control input changed. We will update the FetchTimeRangeDataParams.
     */
    const onTimeRangeControlInputsChanged = useCallback((newStartTime: number, newEndTime: number) => {
        setCurrentViewStates((currentState) => {
            return produce(currentState, (draft) => {
                if (!draft.timeRangeParams) {
                    draft.timeRangeParams = {
                        startTime: newStartTime,
                        endTime  : newEndTime,
                    };
                } else {
                    draft.timeRangeParams.startTime = newStartTime;
                    draft.timeRangeParams.endTime = newEndTime;
                }
            });
        });
    }, []);



    /**
     * Create a fetchDataParams based on the values from the time control we are currently showing. If we are showing the time range control,
     * return the timeRangeParams. If we are showing the time span control, return the timeSpanParams.
     * We will be passing this fetchDataParams object to the EntityUIComponents to tell them how to fetch data.
     */
    const fetchDataParams = useMemo((): FetchEntityTimeSpanDataParams | FetchEntityTimeRangeDataParams | undefined => {
        if (isTimeRangeControlSettings(timeControlSettings)) {
            return timeRangeParams;
        }
        if (isFixedTimeRangeControlSettings(timeControlSettings)) {
            return getDataParamsForFixedTimeRange(
                timeControlSettings.timeRangeType ?? PredefinedTimeRangeType.CURRENT_DAY,
                timeControlSettings.startValue,
            );
        }
        if (isFixedTimeSpanControlSettings(timeControlSettings)) {
            return getDataParamsForFixedTimeSpan(
                timeControlSettings.timeSpan ?? TimeSpan.DAY,
                timeControlSettings.defaultTimeSpanOffset,
            );
        }
        if (timeSpanParams) {
            return timeSpanParams;
        }
        return undefined;
    }, [timeControlSettings, timeSpanParams, timeRangeParams]);


    const controlBar = useMemo(() => {
        return (
            <div className={classes.breadcrumbAndControlBar}>
                <EntityDetailsBreadcrumbBar
                    className={clsx(classes.breadcrumbBar)}
                    projectId={props.projectId}
                    entitiesBreadcrumbs={entitiesBreadcrumbs}
                    onGotoEntity={onEntitySelected}
                    showEntitiesTreeButton
                    onShowEntitiesTreeButtonClick={onShowEntitiesTreeButtonClick}
                    showEntityInfoButton
                    onShowEntityInfoButtonClick={() => {
                        onShowEntityInfoButtonClick(selectedEntityId);
                    }}
                    showEntitySubscriptionButton={selectedViewConfig?.viewSettings.isReportView !== false}
                    onShowEntitySubscriptionButtonClick={onShowEntitySubscriptionButtonClick}
                />

                {/*
                    The EntityDetailsControlBar has inputs that doesn't make sense for any views that are not for Report/KPI
                    therefore we will have an option in the 'viewSettings' to specify if a view is a reportView. By default
                    we will assume all views are report views unless this flag is specifically set to false.
                */}
                {selectedViewConfig && isEntityDetailsTimeSpanControlSettings(timeControlSettings) && timeSpanParams && (
                    <EntityDetailsTimeSpanControlBarContainer
                        key={`${selectedEntityId}-${selectedViewIndex}`}
                        className={clsx(classes.controlBar)}
                        projectId={props.projectId}
                        entityId={selectedEntityId}
                        showSubscriptionSettings={selectedViewConfig?.viewSettings.isReportView !== false}
                        timeControlSettings={timeControlSettings}
                        timeSpan={timeSpanParams.timeSpan}
                        timestamps={timeSpanParams.timestamps}
                        onInputsChanged={onTimeSpanControlInputsChanged}
                    />
                )}

                {selectedViewConfig && isTimeRangeControlSettings(timeControlSettings) && timeRangeParams && (
                    <TimeRangeControlBarContainer
                        key={`${selectedEntityId}-${selectedViewIndex}`}
                        className={clsx(classes.controlBar)}
                        projectId={props.projectId}
                        timeControlSettings={timeControlSettings}
                        startTime={timeRangeParams.startTime}
                        endTime={timeRangeParams.endTime}
                        aggregation={timeRangeParams.aggregation}
                        onInputsChanged={onTimeRangeControlInputsChanged}
                    />
                )}
            </div>
        );
    }, [classes.breadcrumbAndControlBar, classes.breadcrumbBar, classes.controlBar, entitiesBreadcrumbs, onEntitySelected,
        onShowEntitiesTreeButtonClick, onShowEntityInfoButtonClick, onShowEntitySubscriptionButtonClick, onTimeRangeControlInputsChanged,
        onTimeSpanControlInputsChanged, props.projectId, selectedEntityId, timeRangeParams,
        timeSpanParams, selectedViewConfig, selectedViewIndex, timeControlSettings]);


    /**
     * This useEffect is used for attaching the globalSearchBar to the Top Nav bar when this component initialize and also
     * remove the globalSearchBar when this component is destroyed
     */
    useEffect(() => {
        // Only attach the globalSearchBar to the top nav if we want it to be sticky
        if (props.stickyControlBar) {
            uiDispatch(addControlPanelComp(ENTITY_DETAILS_MAIN_CONTROL_BAR, controlBar, ControlPanelCompPriority.HIGH));
        }

        // Return a clean up function which to remove the globalSearchBar when the component is destroyed
        return () => {
            if (props.stickyControlBar) {
                uiDispatch(removeControlPanelComp(ENTITY_DETAILS_MAIN_CONTROL_BAR));
            }
        };
    }, [controlBar, props.stickyControlBar, uiDispatch]);


    return (
        <>
            {project && selectedEntity && selectedEntityClass ? (
                <div className={classes.root}>
                    {!props.stickyControlBar && (
                        controlBar
                    )}

                    <div className={classes.titleContainer}>
                        {selectedViewConfig?.viewSettings.showEntityName !== false && (
                            <div className={classes.title}>
                                <>{selectedEntity.displaySettings.name}</>
                            </div>
                        )}

                        {/*
                            If there is no Entity Name displayed, the Entity view's tabs will be pushed to the left therefore, we need to
                            create a dummy element to make the tabs stays on the right
                        */}
                        {selectedViewConfig?.viewSettings.showEntityName === false && entityClassViewsConfig.length > 1 && (
                            <div />
                        )}

                        {/* Create the Tabs for the entityClassViews but only if there are more than 1 views */}
                        {entityClassViewsConfig.length > 1 && (
                            <Tabs
                                key={entityClassViewsConfig.length}
                                className={classes.viewTabs}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="scrollable"
                                scrollButtons="auto"
                                value={Math.min(selectedViewIndex, entityClassViewsConfig.length - 1)}
                                onChange={onEntityViewTabChange}
                            >
                                {entityClassViewsConfig.map((view, index) => {
                                    return (
                                        <Tab
                                            key={view.viewSettings.displayNameKey ?? DEFAULT_ENTITY_VIEW_DISPLAY_NAME_KEY}
                                            value={index}
                                            icon={view.viewSettings.icon
                                                ? <FontIcon className={classes.tabIcon} iconName={view.viewSettings.icon} /> : undefined}
                                            label={trans(view.viewSettings.displayNameKey ?? DEFAULT_ENTITY_VIEW_DISPLAY_NAME_KEY)}
                                        />
                                    );
                                })}
                            </Tabs>
                        )}
                    </div>

                    {/**
                      * Wait until we have "fetchDataParams" before creating the entity details component. However, sometimes we don't have
                      * fetchDataParams. This is because some entity classes don't have Time Control settings. So if "fetchDataParams" is undefined
                      * and timeControlSettings is also undefined then we can assume fetchDataParams won't be available for this widget and can
                      * continue showing the entity details component. Don't need to wait for "fetchDataParams" to be ready.
                      */}
                    {selectedViewConfig && (fetchDataParams || (!fetchDataParams && !timeControlSettings)) && (
                        <div className={classes.entityDetailsContent}>
                            <EntityDetailsContent
                                projectId={props.projectId}
                                entityId={selectedEntityId}
                                viewConfig={selectedViewConfig}
                                onShowEntityDetails={onEntitySelected}
                                onShowEntityPreview={onShowEntityInfoButtonClick}
                                fetchDataParams={fetchDataParams}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent('EntityDetailsContainer', EntityDetailsContainer);
